import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';
import { getAIAssistantResponse, initializeMcpSession, callMcpToolWithFiles, getMcpSessionForConversation, initializeOAuth, } from '../../../../utils/AIAssistantAPIUtils';
/**
 * Hook for handling AI assistant API responses only
 * Handles the 2-step process: initialize MCP server + invoke Friday
 * @param userMessage - The message to send to the AI assistant
 * @param conversationId - The conversation ID
 * @param enabled - Whether the query should be enabled
 * @param files - Optional files to upload with the message
 * @param shipmentId - Optional shipment ID for context
 */
export function useAIAssistantAPI(userMessage, conversationId, enabled = false, files = [], shipmentId) {
    const query = useQuery({
        queryKey: ['ai-assistant-api', userMessage, conversationId, files.length],
        queryFn: async () => {
            if (!userMessage && files.length === 0) {
                throw new Error('No message or files provided');
            }
            if (!conversationId) {
                throw new Error('No conversation started');
            }
            // Get session ID for this conversation
            const sessionId = await getMcpSessionForConversation(conversationId);
            if (files.length > 0) {
                // Use callMcpToolWithFiles for file uploads - files already have base64 data
                const toolArgs = {
                    message: userMessage || 'Please analyze the uploaded files',
                    conversationId: conversationId,
                };
                // Add shipment context if available
                if (shipmentId) {
                    toolArgs.shipmentId = shipmentId;
                }
                const result = await callMcpToolWithFiles(sessionId, 'friday', toolArgs, files, // Pass files directly - they already have base64 data
                null, conversationId);
                if (result.error) {
                    throw new Error(`MCP Error: ${result.error.message || 'Unknown error'}`);
                }
                // Extract response content safely
                let response;
                if (typeof result.result === 'string') {
                    response = result.result;
                }
                else if (typeof result.result === 'object' &&
                    result.result !== null) {
                    // Check if the object has content or message properties
                    if ('content' in result.result &&
                        typeof result.result.content === 'string') {
                        response = result.result.content;
                    }
                    else if ('message' in result.result &&
                        typeof result.result.message === 'string') {
                        response = result.result.message;
                    }
                    else {
                        response = '';
                    }
                }
                else {
                    response = '';
                }
                return {
                    response,
                    data: result.result,
                };
            }
            else {
                // Use the regular getAIAssistantResponse for text-only messages
                const utilsResponse = await getAIAssistantResponse(userMessage, conversationId, shipmentId);
                // Convert utils response format to hook response format
                return {
                    response: utilsResponse.response,
                    data: utilsResponse.data,
                };
            }
        },
        enabled: (!!userMessage || files.length > 0) && enabled, // Run when there's a message OR files AND enabled
        retry: 0, // Retry once on failure
        staleTime: 0, // Always consider data stale for new responses
        // Don't run on mount
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
    return {
        data: query.data,
        isLoading: query.isLoading && enabled, // Only show loading when we're actually querying
        error: query.error,
        isError: query.isError,
        refetch: query.refetch,
    };
}
/**
 * Hook for initializing MCP session for a conversation
 * @param {string} conversationId - The conversation ID
 * @param {boolean} enabled - Whether to enable the session initialization
 */
export function useMcpSession(conversationId, enabled = false) {
    var _a;
    const query = useQuery({
        queryKey: ['mcp-session', conversationId],
        queryFn: async () => {
            const session = await initializeMcpSession();
            return session;
        },
        enabled: enabled && !!conversationId, // Only run when enabled and conversationId exists
        staleTime: Infinity, // Keep session alive for the entire conversation
        retry: 1, // Retry once on failure
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });
    return {
        sessionId: ((_a = query.data) === null || _a === void 0 ? void 0 : _a.id) || null,
        isSessionLoading: query.isLoading,
        sessionError: query.error,
        isSessionError: query.isError,
    };
}
/**
 * Hook for managing AI assistant chat with TanStack Query using MCP
 * Handles the 2-step process: initialize MCP server + invoke Friday
 * @deprecated Use useAIAssistantAPI instead for API-only concerns
 */
export function useAIAssistantQuery(userMessage, shouldQuery, conversationId) {
    return useAIAssistantAPI(userMessage, conversationId, shouldQuery);
}
function extractResponseText(data) {
    var _a;
    // Handle different response formats from AI assistant
    if (typeof data === 'string') {
        return data;
    }
    // Handle new response format with response array
    if ((data === null || data === void 0 ? void 0 : data.response) && Array.isArray(data.response)) {
        const textParts = data.response
            .filter((item) => item.type === 'text' && item.text)
            .map((item) => item.text);
        if (textParts.length > 0) {
            return textParts.join('\n');
        }
    }
    // Handle data.content array format
    if (((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.content) && Array.isArray(data.data.content)) {
        const textParts = data.data.content
            .filter((item) => item.type === 'text' && item.text)
            .map((item) => item.text);
        if (textParts.length > 0) {
            return textParts.join('\n');
        }
    }
    // Handle legacy string response format
    if ((data === null || data === void 0 ? void 0 : data.response) && typeof data.response === 'string') {
        return data.response;
    }
    if ((data === null || data === void 0 ? void 0 : data.message) && typeof data.message === 'string') {
        return data.message;
    }
    // Fallback: stringify the entire response
    return JSON.stringify(data, null, 2);
}
/**
 * Hook for managing AI assistant chat state and messages with upfront session initialization
 * @param {boolean} isOpen - Whether the chat drawer is open
 * @param {string} shipmentId - Optional shipment ID for context
 */
export function useAIChatWithQuery(isOpen = false, shipmentId) {
    const [messages, setMessages] = React.useState([]);
    const [input, setInput] = React.useState('');
    const [pendingMessage, setPendingMessage] = React.useState(null);
    const [pendingFiles, setPendingFiles] = React.useState([]);
    const [shouldQuery, setShouldQuery] = React.useState(false);
    const scrollAreaRef = React.useRef(null);
    const [conversationId, setConversationId] = React.useState(uuidv4());
    // Initialize MCP session when chat drawer opens
    const { sessionId, isSessionLoading, sessionError } = useMcpSession(conversationId, isOpen);
    // Query for AI response when there's a pending message
    const { data: aiResponse, isLoading, error, isError, } = useAIAssistantAPI(pendingMessage, conversationId, shouldQuery && !!sessionId, pendingFiles, shipmentId);
    // Auto-scroll to bottom when messages change
    React.useEffect(() => {
        if (scrollAreaRef.current) {
            scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
        }
    }, [messages]);
    // Handle AI response and errors when they come back
    React.useEffect(() => {
        if (!pendingMessage)
            return; // No pending message to process
        // Priority: if we have data, use it (even if there was a previous error)
        if (aiResponse) {
            // Success case - we have data
            const responseText = extractResponseText(aiResponse);
            const aiMessage = {
                id: Date.now().toString(),
                role: 'assistant',
                content: responseText,
            };
            setMessages((prev) => [...prev, aiMessage]);
            setPendingMessage(null); // Clear pending message
            setShouldQuery(false); // Reset query flag
        }
        else if (isError && !aiResponse) {
            // Error case - only if we don't have data
            const errorMessage = {
                id: Date.now().toString(),
                role: 'assistant',
                content: 'Sorry, I encountered an error while processing your request. Please try again.',
            };
            setMessages((prev) => [...prev, errorMessage]);
            setPendingMessage(null); // Clear pending message
            setShouldQuery(false); // Reset query flag
        }
    }, [aiResponse, isError, pendingMessage]);
    const retryMessage = async (message) => {
        if (isLoading || isSessionLoading)
            return;
        const userMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: message,
        };
        // Retry removes the last two messages to retry the original prompt?
        setMessages((prev) => {
            const reversed = [...prev].reverse();
            const index = reversed.findIndex((msg) => msg.role === 'user');
            if (index === -1)
                return [...prev, userMessage]; // fallback
            const cutIndex = prev.length - 1 - index;
            return [
                ...prev.slice(0, cutIndex),
                ...prev.slice(cutIndex + 1),
                userMessage,
            ];
        });
        setInput('');
        setPendingMessage(userMessage.content); // Set the message
        setShouldQuery(true); // Trigger the query
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!input.trim() || isLoading || isSessionLoading)
            return;
        const userMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: input.trim(),
        };
        setMessages((prev) => [...prev, userMessage]);
        setInput('');
        setPendingMessage(userMessage.content); // Set the message to be processed
        setShouldQuery(true); // Trigger the query
    };
    const handleSubmitWithFiles = async (e, files = []) => {
        e.preventDefault();
        if ((!input.trim() && files.length === 0) || isLoading || isSessionLoading)
            return;
        const messageContent = input.trim();
        const userMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: messageContent ||
                (files.length > 0 ? `Uploaded ${files.length} file(s)` : ''),
        };
        setMessages((prev) => [...prev, userMessage]);
        setInput('');
        setPendingMessage(messageContent);
        setPendingFiles(files);
        setShouldQuery(true);
    };
    const handleInputChange = (e) => {
        setInput(e.target.value);
    };
    const initOAuth = (e) => {
        e.preventDefault();
        initializeOAuth();
    };
    const clearMessages = () => {
        setMessages([]);
        setPendingMessage(null);
        setPendingFiles([]);
        setShouldQuery(false);
        // Generate new conversation ID to get a fresh session
        setConversationId(uuidv4());
    };
    return {
        messages,
        input,
        isLoading: isLoading || isSessionLoading,
        error: error || sessionError,
        isError: isError || !!sessionError,
        scrollAreaRef,
        handleSubmit,
        handleSubmitWithFiles,
        handleInputChange,
        clearMessages,
        retryMessage,
        sessionId,
        isSessionReady: !!sessionId && !isSessionLoading,
        initOAuth,
    };
}
